/**
 * Post-checkout verification policy for wallet top-ups (pure — unit-tested).
 *
 * Industry practice (Stripe / Razorpay / Cashfree): the redirect back from
 * checkout is NOT proof of anything. The page asks OUR server, which asks the
 * gateway, and keeps asking on a backoff until the answer is final:
 *
 *   credited / failed / cancelled / expired → final, stop.
 *   awaiting   → the gateway may lag the redirect by a few seconds; give it a
 *                short window, then report "payment not completed".
 *   processing → bank / UPI still confirming: keep checking longer, then hand
 *                off ("we'll credit automatically — don't pay again").
 *   crediting  → paid, credit being applied: short window, then hand off.
 *   transport errors never become "failed": they just consume the budget and
 *                end as "couldn't confirm right now — check again".
 */

import type { TopupState } from '@/services/wallet.api';

export const FINAL_STATES: ReadonlySet<TopupState> = new Set(['credited', 'failed', 'cancelled', 'expired']);

/** How long each non-final state is worth waiting for before handing off to the user. */
export const STATE_BUDGET_MS: Record<'awaiting' | 'processing' | 'crediting' | 'unknown', number> = {
  awaiting: 12_000,
  processing: 120_000,
  crediting: 30_000,
  unknown: 30_000,
};

/** Backoff: fast first checks (most payments resolve in seconds), then slower. */
const SCHEDULE_MS = [1_500, 2_000, 2_500, 3_000, 4_000, 5_000, 6_000, 8_000];
const MAX_DELAY_MS = 10_000;

export function backoffDelay(attempt: number): number {
  return SCHEDULE_MS[attempt] ?? MAX_DELAY_MS;
}

/**
 * Delay before the next check, or null to stop polling.
 * `state` is the last server answer ('unknown' = the last call errored).
 */
export function nextPollDelay(state: TopupState | 'unknown', attempt: number, elapsedMs: number): number | null {
  if (state !== 'unknown' && FINAL_STATES.has(state)) return null;
  const budget = STATE_BUDGET_MS[state as keyof typeof STATE_BUDGET_MS] ?? STATE_BUDGET_MS.unknown;
  if (elapsedMs >= budget) return null;
  return Math.min(backoffDelay(attempt), Math.max(0, budget - elapsedMs));
}

// ── Pending-order memory ──────────────────────────────────────────────────────
// Survives the full-page redirect AND a user who closes the result early: the
// next visit to the wallet page re-checks the order instead of forgetting it.

const PENDING_KEY = 'baatly.wallet.pendingTopup';
const PENDING_TTL_MS = 24 * 3600 * 1000;

export interface PendingTopup {
  gatewayOrderId: string;
  amount: number;
  at: number;
}

export function rememberPendingTopup(p: Omit<PendingTopup, 'at'>): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ ...p, at: Date.now() }));
  } catch {
    /* storage unavailable — the ?order_id redirect still works */
  }
}

export function readPendingTopup(): PendingTopup | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingTopup;
    if (!p?.gatewayOrderId || Date.now() - Number(p.at) > PENDING_TTL_MS) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

export function clearPendingTopup(gatewayOrderId?: string): void {
  try {
    const p = readPendingTopup();
    if (!gatewayOrderId || p?.gatewayOrderId === gatewayOrderId) localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

// ── Amount validation (mirrors the backend's createOrderForWalletTopup) ───────

export function validateTopupAmount(raw: string, min: number, max: number): { amount?: number; error?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { error: 'Enter an amount' };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return { error: 'Enter a valid amount (up to 2 decimals)' };
  const amount = Number(trimmed);
  if (!Number.isFinite(amount) || amount <= 0) return { error: 'Enter a valid amount' };
  if (amount < min) return { error: `Minimum top-up is ₹${min.toLocaleString('en-IN')}` };
  if (amount > max) return { error: `Maximum top-up is ₹${max.toLocaleString('en-IN')}` };
  return { amount };
}
